import MTrip from './MTrip';
import MSituation from './MSituation';

class OTripRequest {
  constructor(element) {
    this.element = element;
    this.loadingIndicatorElement = element.querySelector('.a-loading-indicator');
    this.formElement = element.querySelector('form');
    this.errorElement = element.querySelector('.o-trip-request__error');
    this.tripsElement = element.querySelector('.o-trip-request__trips');
    this.tripsListElement = Object.assign(document.createElement('ul'), {
      role: 'list',
    });
    this.apiUrl = element.dataset.apiUrl;

    this.mTrips = [];
    this.mSituations = [];

    const fromDataValue = this.formElement.fromData.value;
    const toDataValue = this.formElement.toData.value;
    const from = JSON.parse(fromDataValue !== '' ? fromDataValue : null);
    const to = JSON.parse(toDataValue !== '' ? toDataValue : null);
    const date = this.formElement.date.value;
    const time = this.formElement.time.value;
    const datetimeFor = this.formElement.datetimeFor.value;
    const datetime = `${date}T${time}`;
    const excludeModes = [...this.formElement.querySelectorAll('input[type="checkbox"][name="modes[]"]')]
      .filter(_ => !_.checked).map(_ => _.value);

    // init
    this.tripsElement.appendChild(this.tripsListElement);
    if (from && to) {
      this.updateTrips(from, to, datetime, datetimeFor, excludeModes);
    } else {
      console.info('no trip request', this.formElement.elements);
    }
  }

  setFrom(location) {
    if (location) {
      const {
        from,
        fromData,
      } = this.formElement;
      from.removeAttribute('aria-activedescendant');
      from.value = location.name;
      fromData.value = JSON.stringify(location);
      fromData.disabled = false;
      from.dispatchEvent(new Event('automaticInput', {
        bubbles: true,
        cancelable: true,
      }));
      from.scrollIntoView();
    }

    /* eslint-disable-next-line no-underscore-dangle */
    window._paq?.push(['trackEvent', 'Verbindungsauskunft', 'Click', 'setFromStop']);
  }

  setTo(location) {
    if (location) {
      const {
        to,
        toData,
      } = this.formElement;
      to.removeAttribute('aria-activedescendant');
      to.value = location.name;
      toData.value = JSON.stringify(location);
      toData.disabled = false;
      to.dispatchEvent(new Event('automaticInput', {
        bubbles: true,
        cancelable: true,
      }));
      to.scrollIntoView();

      /* eslint-disable-next-line no-underscore-dangle */
      window._paq?.push(['trackEvent', 'Verbindungsauskunft', 'Click', 'setToStop']);
    }
  }

  get from() {
    return this.formElement.from.value;
  }

  get to() {
    return this.formElement.to.value;
  }

  async prepareForPrint() {
    const { mMap } = window;
    const currentTrip = this.mTrips.find((mTrip) => mTrip.isCurrent);
    if (currentTrip) {
      currentTrip.expand();

      // prepare map
      Object.assign(mMap.element.style, {
        width: '1000px',
        height: '1000px',
      });
      mMap.map.resize();
      currentTrip.showRoute(true);
      // wait for new images to be loaded
      await new Promise((resolve) => {
        setTimeout(resolve, 500);
      });

      mMap.generateImg();

      // reset
      Object.assign(mMap.element.style, {
        width: null,
        height: null,
      });
      mMap.map.resize();
    }
    return true;
  }

  createTrips(data) {
    const tripResults = Array.isArray(data['trias:TripResult']) ? data['trias:TripResult'] : [data['trias:TripResult']];
    tripResults.forEach((tripResultStructure, index) => {
      const mTrip = new MTrip(tripResultStructure, index);
      this.mTrips.push(mTrip);
      this.tripsListElement.appendChild(mTrip.element);
    });
    this.tripsElement.removeAttribute('hidden');
  }

  createSituations(data) {
    let situations = data['trias:TripResponseContext']?.['trias:Situations']?.['trias:PtSituation'];
    if (situations) {
      situations = Array.isArray(situations) ? situations : [situations];
      situations.forEach((situation) => {
        this.mSituations.push(new MSituation(situation));
      });
    }
  }

  showError(message) {
    this.errorElement.textContent = message;
    this.errorElement.removeAttribute('hidden');

    /* eslint-disable-next-line no-underscore-dangle */
    window._paq?.push(['trackEvent', 'o-trip-request', 'error', message]);
  }

  async updateTrips(from, to, datetime = null, datetimeFor = null, excludeModes = null) {
    // reset
    this.errorElement.innerHTML = '';
    this.errorElement.setAttribute('hidden', '');
    this.tripsListElement.innerHTML = '';
    this.tripsElement.setAttribute('hidden', '');
    this.mTrips.forEach((mTrip) => mTrip.destroy());
    this.mTrips = [];
    this.mSituations = [];

    let json = null;
    let data = null;
    let status = null;
    try {
      json = await this.fetchTrips(from, to, datetime, datetimeFor, excludeModes);
      status = json.status;
      data = json.data;

      const fromText = this.formElement.from.value;
      const toText = this.formElement.to.value;
      /* eslint-disable-next-line no-underscore-dangle */
      window._paq?.push(['trackEvent', 'Verbindungsauskunft', 'fromTo', `${fromText} – ${toText}`, data.length]);
      /* eslint-disable-next-line no-underscore-dangle */
      window._paq?.push(['trackEvent', 'Verbindungsauskunft', 'from', `${fromText}`, data.length]);
      /* eslint-disable-next-line no-underscore-dangle */
      window._paq?.push(['trackEvent', 'Verbindungsauskunft', 'to', `${toText}`, data.length]);
    } catch (exception) {
      this.showError('Allgemeiner Fehler bei der Verbindungsauskunft.');
    }
    if (data?.['trias:ErrorMessage']) {
      const errorMessage = data['trias:ErrorMessage']?.['trias:Text']?.['trias:Text'];
      this.showError(errorMessage);
    } else if (status === 'error') {
      this.showError(json.message);
    } else if (data?.['trias:TripResult']) {
      this.createSituations(data);
      this.createTrips(data);

      // select first trip
      if (window.mMap?.map.loaded()) {
        this.mTrips?.[0]?.setCurrent();
      }
    } else {
      this.showError('Fataler Fehler bei der Verbindungsauskunft.');
    }
  }

  async fetchTrips(from, to, datetime = null, datetimeFor = null, excludeModes = null) {
    this.loadingIndicatorElement.removeAttribute('hidden');
    const response = await fetch(this.apiUrl, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        from,
        to,
        datetime,
        datetimeFor,
        excludeModes,
      }),
    });
    this.loadingIndicatorElement.setAttribute('hidden', '');

    /* eslint-disable-next-line no-underscore-dangle */
    window._paq?.push(['trackEvent', 'Verbindungsauskunft', 'fetchTrips', response.headers.get('Server-Timing')]);

    return response.json();
  }
}

const element = document.querySelector('.o-trip-request');
if (element) {
  window.oTripRequest = new OTripRequest(element);
}
